import {
  AuthLocalStorage,
  getProfilesFromSessionStorage,
  getToken,
  Profile,
} from 'application/sessionStorage/auth';
import axios from 'axios';
import { usersRefreshToken } from 'application/store/reducers/Users/ActionCreators';
import dayjs from 'dayjs';
import { store } from 'application/store';
import { ApplicationType, x_app_type } from '../../application';

/**
 * Creates an instance of Axios with a base URL set to the value of process.env.REACT_APP_BASE_URL.
 */
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

// Add a request interceptor
axiosInstance.interceptors.request.use(
  async function (config) {
    const profile: Profile[] = getProfilesFromSessionStorage()!;
    const date = localStorage.getItem(AuthLocalStorage.accessTokenExpiresAt);
    const minusMinuteDate = date
      ? dayjs(date).subtract(1, 'minute').toISOString()
      : null;

    // Checks if the date is valid and whether the current time is within one minute of the token expiration.
    const isDateValid =
      dayjs().isValid() && dayjs(minusMinuteDate).diff(dayjs(), 'minute') <= 59;

    // Checks if the token has expired.
    const isExpiredToken = minusMinuteDate
      ? dayjs().isAfter(minusMinuteDate)
      : false;

    if (!isDateValid || isExpiredToken) {
      if (!profile?.length) {
        return {
          ...config,
        };
      }

      // Sends a request to refresh the token.
      const data = await store.dispatch(
        usersRefreshToken({
          refreshToken: profile[0].refreshToken,
          email: profile[0].email,
        }),
      );

      if (data && data.meta.requestStatus === 'fulfilled') {
        return {
          ...config,
          ...{
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-type': 'Application/json',
              Authorization: `Bearer ${getToken()}`,
              [x_app_type]: ApplicationType.AdminWeb,
            },
          },
        };
      }
    }

    // Do something before request is sent

    const token = getToken();
    if (!token) return;

    return {
      ...config,
      ...(token
        ? {
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-type': 'Application/json',
              Authorization: `Bearer ${token}`,
              [x_app_type]: ApplicationType.AdminWeb,
            },
          }
        : {}),
    };
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  },
);

// Add a response interceptor
axiosInstance.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    return Promise.reject(error);
  },
);

export default axiosInstance;
